import type { QuestionnaireStatuses } from './schemas/questionnaire-create.js';
import { QUESTIONNAIRE_REQUEST_STATUSES } from './schemas/questionnaire-request-status.js';

/**
 * Questionnaire constants shared by the backend and both frontends.
 *
 * **Why here and not in `../constants.ts`.** That file is the module *root*'s
 * constants — AMQP routing keys — and is exported only from
 * `questionnaires/index.ts`, which drags `core/**` and `node:*` with it. A
 * frontend must never import that entry point (`modules/CLAUDE.md`), so anything
 * both sides need lives under `contracts/`, which is zod-only. This mirrors
 * `order-hub`, the other split module whose frontend consumes runtime constants.
 *
 * **What belongs here:** a value set that is questionnaire business logic, or
 * that already exists on both sides. Presentation (badge colours, i18n keys,
 * English labels) and client-only models (builder modes, library tabs) stay in
 * the frontend — a shared constant should never carry a display string.
 *
 * Some tuples deliberately live beside the schema that owns them —
 * `QuestionnaireStatuses`, `QUESTIONNAIRE_REQUEST_STATUSES`,
 * `QUESTION_RESPONSE_STATUSES`, `REQUEST_ASSET_BUCKETS`,
 * `REQUEST_ASSET_FILE_PARTS` — and reach consumers through the same barrel.
 * This file holds the shared constants with no single owning schema, plus the
 * *named members* of those tuples, which `core/` was re-declaring per file.
 */

/**
 * `is_retraced` is a `varchar` holding `'YES'` / `'NO'`, never a boolean.
 *
 * Declared as an enum for the same reason `status` is: the platform enforces
 * value domains in the application layer rather than with database CHECK
 * constraints, so the absence of a constraint is the convention, not evidence
 * that anything may be stored. Staging holds `NO` (1625) and `YES` (15) and
 * nothing else.
 *
 * Lives here rather than beside the list schema that reads it: the flag is not
 * list-specific, and five services need the individual members.
 */
export const RetracedFlags = ['YES', 'NO'] as const;

export type RetracedFlag = (typeof RetracedFlags)[number];

/**
 * The two `is_retraced` members, named.
 *
 * `const RETRACED_OWNED = 'YES'` was declared identically in **five** services
 * and `COMPANY_OWNED = 'NO'` in two. The value is a domain fact, not a
 * per-service detail.
 */
export const RETRACED_OWNED: RetracedFlag = 'YES';
export const COMPANY_OWNED: RetracedFlag = 'NO';

/**
 * The `question_type_id` vocabulary.
 *
 * Twenty-two of these are values staging actually holds (counts below).
 * `MULTIPLE_CHOICE` is the twenty-third: staging has none today, but it is in
 * the legacy vocabulary and both `answer-humanizer.ts` and the old dashboard
 * still branch on it, so leaving it out would make the catalogue narrower than
 * the code that reads it.
 *
 * **A catalogue, not a wire constraint.** `questionTypeId` is deliberately
 * `z.string()` on both read (`questionnaire-builder.ts`) and write
 * (`questionnaire-builder-update.ts`), and it must stay that way: the column is
 * open, carries a decade of legacy values, and a question with an unrecognised
 * type has to keep round-tripping rather than fail validation. Turning this into
 * a `z.enum` would 500 on real rows.
 *
 * What it is for is agreement: before this existed, the vocabulary was implied
 * by three partial, mutually inconsistent copies —
 * `frontend-v2`'s `BUILDER_ANSWER_TYPES` (14 values), the old dashboard's
 * `supplierQuestionnaireAnswerTypes` (14, a different 14), and
 * `QUESTION_TYPES` in `core/services/answer-humanizer.ts` (8, and deliberately
 * partial: it is that humaniser's branch map, not a vocabulary).
 *
 * Counts are from staging at the time of writing, and are here so the next
 * person can see which values are load-bearing rather than guess. They are a
 * snapshot, not a constraint — nothing validates against them:
 *
 * ```
 * SHORT_TEXT 6032 · NUMBER 4379 · YES_NO 4035 · SELECT 3536 · LONG_TEXT 2782
 * CHECK_BOX 1459 · DOCUMENTS 1409 · MULTI_SELECT 1203 · CHECK_BOXES 1202
 * CERTIFICATE 1094 · TABLE 782 · NUMBER_UOM 428 · AUDIT 414
 * NUMBER_DYNAMIC_UOM_WITH_YEAR 235 · DATE 214
 * NUMBER_DYNAMIC_CURRENCY_WITH_YEAR 202 · MULTIPLE_PHOTO_UPLOAD 147
 * SELECT_WITH_FOLLOW_UP 122 · NUMBER_DYNAMIC_UOM 115 · VIDEO_UPLOAD 96
 * BOOLEAN 20 · ASSET_LINK 6
 * ```
 */
export const QUESTION_ANSWER_TYPES = [
  'SHORT_TEXT',
  'LONG_TEXT',
  'NUMBER',
  'NUMBER_UOM',
  'NUMBER_DYNAMIC_UOM',
  'NUMBER_DYNAMIC_UOM_WITH_YEAR',
  'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR',
  'YES_NO',
  'BOOLEAN',
  'SELECT',
  'MULTI_SELECT',
  'SELECT_WITH_FOLLOW_UP',
  'MULTIPLE_CHOICE',
  'CHECK_BOX',
  'CHECK_BOXES',
  'DATE',
  'TABLE',
  'DOCUMENTS',
  'AUDIT',
  'CERTIFICATE',
  'ASSET_LINK',
  'MULTIPLE_PHOTO_UPLOAD',
  'VIDEO_UPLOAD',
] as const;

export type QuestionAnswerType = (typeof QUESTION_ANSWER_TYPES)[number];

/** True when `value` is a type this platform knows about. */
export const isKnownAnswerType = (
  value: unknown,
): value is QuestionAnswerType =>
  typeof value === 'string' &&
  (QUESTION_ANSWER_TYPES as readonly string[]).includes(value);

/**
 * The question types whose answer names an **existing** asset rather than
 * carrying a value: the answer stores `{ value: [{ optionId, id }] }`, where `id`
 * is an `audits_service_audits`, `company_documents` or `company_claims` row.
 *
 * Shared because the answer-save branches on it to grant the requesting brand
 * access to whatever the answer now names. Sharing used to live only in the three
 * request-scoped *create* routes, so picking an asset that already existed
 * attached it to the brand's questionnaire and granted the brand nothing —
 * DELTA-2507, 436 audits across 39 brands. Creation was the same bug one step
 * earlier (DELTA-2415), which is why the grant now hangs off the link rather than
 * off any one create path.
 *
 * `ASSET_LINK` is deliberately absent: it is the *builder's* generic name for the
 * family, never a stored `question_type_id` on an answered question.
 */
export const ASSET_LINK_QUESTION_TYPES = [
  'DOCUMENTS',
  'AUDIT',
  'CERTIFICATE',
] as const;

export type AssetLinkQuestionType = (typeof ASSET_LINK_QUESTION_TYPES)[number];

/** True when answers to `value` link an existing asset. */
export const isAssetLinkQuestionType = (
  value: unknown,
): value is AssetLinkQuestionType =>
  typeof value === 'string' &&
  (ASSET_LINK_QUESTION_TYPES as readonly string[]).includes(value);

/**
 * The answer types whose `unit` slot holds a **currency code**, not a physical
 * unit of measurement.
 *
 * Every other `NUMBER_*` type puts a UOM there (kilogram, square metre,
 * pieces). These two put `EUR`, `USD`, … in the same slot, so a consumer that
 * gets this wrong offers a picker with no correct option in it.
 *
 * **The authority is the datapoint's `fieldTypeId`, never the question row's
 * stored `question_type_id`.** Those two disagree on real rows: DELTA-2568 found
 * money questions persisted as `NUMBER_UOM`, which made frontend-v2 render the
 * physical-unit catalogue for "living wage in local currency". Because the unit
 * is required to save, the responder was left with a mandatory field and no
 * valid choice, and Save silently did nothing. Resolve the type from the
 * datapoint first, then ask this function.
 *
 * `NUMBER_DYNAMIC_CURRENCY` is not in {@link QUESTION_ANSWER_TYPES}: no question
 * row stores it today, but it is a live datapoint `fieldTypeId` and both
 * frontends already branch on it, so leaving it out would make this narrower
 * than the code that reads it.
 */
export const CURRENCY_ANSWER_TYPES = [
  'NUMBER_DYNAMIC_CURRENCY',
  'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR',
] as const;

export type CurrencyAnswerType = (typeof CURRENCY_ANSWER_TYPES)[number];

/** True when `value`'s unit slot holds a currency code rather than a UOM. */
export const isCurrencyAnswerType = (
  value: unknown,
): value is CurrencyAnswerType =>
  typeof value === 'string' &&
  (CURRENCY_ANSWER_TYPES as readonly string[]).includes(value);

/**
 * The new builder's Pascal-case answer types, mapped to what the column stores.
 *
 * frontend-v2's builder names five types in Pascal case (`ShortText`, …) because
 * its own schema does; every other type it handles already uses the wire
 * spelling. This table is what keeps the two vocabularies in step, and it is
 * **load-bearing on the backend side**: `questionnaire-builder-update` diffs
 * `existingQuestion.questionTypeId !== newQuestion.questionTypeId` to decide
 * whether a question on an active questionnaire may be edited, so a value that
 * does not map cleanly makes that guard fire on an unchanged question.
 *
 * It lived in `frontend-v2`'s `builderSerializers.ts` until this move. It is
 * here now because the definition of "correct" belongs to the backend.
 */
export const ANSWER_TYPE_TO_WIRE: Readonly<Record<string, QuestionAnswerType>> =
  {
    ShortText: 'SHORT_TEXT',
    LongText: 'LONG_TEXT',
    YesOrNo: 'YES_NO',
    Number: 'NUMBER',
    Select: 'SELECT',
  };

/**
 * Normalises a builder answer type to the value the column stores.
 *
 * **The fallback is the sharp edge.** An unmapped type is passed through
 * unchanged, which is what lets the already-wire-spelled types (`DOCUMENTS`,
 * `TABLE`, …) survive — but it also means a *new* Pascal-case UI type added
 * without an entry above would be persisted verbatim, and nothing would reject
 * it: `questionTypeId` is `z.string()` by design. `constants.unit.test.ts`
 * pins that every mapped output is a known type; the fallback is why that test
 * exists.
 */
export const toWireAnswerType = (answerType: string): string =>
  ANSWER_TYPE_TO_WIRE[answerType] ?? answerType;

/**
 * The request statuses a person may actually set.
 *
 * `QUESTIONNAIRE_REQUEST_STATUSES` is the column's full domain and includes
 * `DELETED`, which is written **only** by the bulk-delete path — alongside
 * `is_removed = 1`, in the same statement — and is therefore never a choice
 * anyone makes. Both frontends had already encoded that by hand-declaring a
 * four-value set; naming it here makes "never offer DELETE where a status can be
 * changed" a shared rule instead of a convention each app re-derives.
 *
 * Use this for anything a user picks from. Use the full tuple for anything that
 * describes what a row may *contain*.
 */
export const RESPONDER_SETTABLE_REQUEST_STATUSES =
  QUESTIONNAIRE_REQUEST_STATUSES.filter(
    (
      status,
    ): status is Exclude<
      (typeof QUESTIONNAIRE_REQUEST_STATUSES)[number],
      'DELETED'
    > => status !== 'DELETED',
  );

export type ResponderSettableRequestStatus =
  (typeof RESPONDER_SETTABLE_REQUEST_STATUSES)[number];

/**
 * The settable request statuses as a keyed map.
 *
 * The same four values as {@link RESPONDER_SETTABLE_REQUEST_STATUSES}, in the
 * shape a caller reaches for by name (`SETTABLE_REQUEST_STATUS.IN_REVIEW`).
 * frontend-v2's `ANSWER_REQUEST_STATUSES` is this.
 *
 * **Deliberately not {@link REQUEST_STATUS}.** That map carries `DELETED`, and
 * anything a person picks from must not — which is precisely the mistake this
 * pair exists to make hard to write.
 */
export const SETTABLE_REQUEST_STATUS = {
  OPEN: 'OPEN',
  IN_REVIEW: 'IN_REVIEW',
  COMPLETED: 'COMPLETED',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
} as const satisfies Record<string, ResponderSettableRequestStatus>;

/**
 * The request statuses, named.
 *
 * Every one of these was a per-file `const` somewhere in `core/` —
 * `REQUEST_STATUS_OPEN` in two files, plus `STATUS_IN_REVIEW`,
 * `STATUS_COMPLETED`, `REQUEST_STATUS_COMPLETED`, `REQUEST_STATUS_DELETED` and
 * a local `REQUEST_STATUS` map. One definition instead of eight.
 */
export const REQUEST_STATUS = {
  OPEN: 'OPEN',
  IN_REVIEW: 'IN_REVIEW',
  COMPLETED: 'COMPLETED',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
  /**
   * Written **only** by the bulk-delete path, alongside `is_removed = 1`. Never
   * offer it as a choice — use {@link RESPONDER_SETTABLE_REQUEST_STATUSES}.
   */
  DELETED: 'DELETED',
} as const satisfies Record<
  string,
  (typeof QUESTIONNAIRE_REQUEST_STATUSES)[number]
>;

/**
 * The questionnaire statuses, named. `DRAFT_STATUS = 'DRAFT'` was declared
 * identically in three services.
 */
export const QUESTIONNAIRE_STATUS = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
} as const satisfies Record<string, (typeof QuestionnaireStatuses)[number]>;

/**
 * The asset kinds an array answer may link to, via its option's `linkedAsset`.
 *
 * Shared because the backend *branches* on it: an element whose `linkedAsset` is
 * not in this set publishes no AMQP claim or company-document event
 * (`core/services/question-response-create.service.ts`). Drift here silently
 * changes behaviour rather than producing an error, which is why both frontends
 * carrying their own copy was a real hazard and not just duplication.
 *
 * Display labels for these keys stay in each frontend — they are presentation,
 * and the old dashboard and frontend-v2 word them differently.
 */
export const LINKED_ASSET_TYPES = [
  'CERTIFICATE',
  'SCOPE',
  'PRODUCT',
  'TRANSACTION',
  'COMPANY',
  'COMPANY_DOCUMENT',
  'MEMBERSHIP',
  'AUDIT',
] as const;

export type LinkedAssetType = (typeof LINKED_ASSET_TYPES)[number];

/**
 * Which `linkedAsset` values each answer-type bucket accepts.
 *
 * `CERTIFICATE` is a meta-bucket — a certificate question accepts any of the six
 * certificate-family kinds. Every value here is a {@link LINKED_ASSET_TYPES}
 * member, which the unit test pins: the two lists diverging is exactly the bug
 * this shared definition prevents.
 */
export const ASSET_BUCKET_ACCEPTS = {
  COMPANY_DOCUMENT: ['COMPANY_DOCUMENT'],
  AUDIT: ['AUDIT'],
  CERTIFICATE: [
    'CERTIFICATE',
    'SCOPE',
    'PRODUCT',
    'TRANSACTION',
    'COMPANY',
    'MEMBERSHIP',
  ],
} as const satisfies Record<string, readonly LinkedAssetType[]>;

export type AssetBucket = keyof typeof ASSET_BUCKET_ACCEPTS;

/**
 * Pagination limits, shared by the query schemas and the SQL that honours them.
 *
 * The default and the cap were inlined in five contract schemas and re-declared
 * as `const DEFAULT_PAGE_SIZE = 15` in three `core/db` list files — the schema
 * and the query that has to agree with it were separate literals.
 *
 * Both numbers are the legacy generator's, not a fresh choice.
 */
export const DEFAULT_PAGE_SIZE = 15;
export const MAX_PAGE_SIZE = 500;

/** Max length of the JSON `filter` query param. */
export const FILTER_MAX_LENGTH = 512;

/**
 * The answer-requests list allows a longer filter than every other list.
 *
 * Deliberate, and kept as its own constant so it cannot be "tidied" into
 * {@link FILTER_MAX_LENGTH}: that list filters on far more columns, so a filter
 * naming several of them exceeds 512 in normal use.
 */
export const REQUEST_FILTER_MAX_LENGTH = 2048;
